import { useLocale, useTranslations } from 'gt-react'
import { useAccountName } from '@/components/user-name'
import { formatDate, formatDateTime } from '@/lib/intl-format'
import { type AuditInfo, useAuditFooter, useFooterPaginationSlot } from './audit-context'

// ISO-8601 week number (weeks start Monday; week 1 contains the first Thursday).
function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
}

// Right-aligned "created / modified by" for the current editor/detail page.
// Mounted only when audit data is present, so useAccountName's directory
// query doesn't run on pages without it. The audit deliberately shows the
// editor's ACCOUNT name (users.name), not their linked-employee name, so a
// user editing their own employee record isn't mistaken for the employee.
function FooterAudit({ audit }: { audit: AuditInfo }) {
  const t = useTranslations()
  const locale = useLocale()
  const tk = (k: string) => t(`common.audit.${k}`)
  const createdByName = useAccountName(audit.createdByUserId)
  const updatedByName = useAccountName(audit.updatedByUserId)
  const fmt = (v: string | null | undefined) => formatDateTime(v, locale)

  return (
    <p className="text-[11px] text-muted-foreground text-right">
      {tk('created')}: {fmt(audit.createdAt)}
      {audit.createdByUserId ? ` ${tk('by')} ${createdByName}` : ''}
      {'  ·  '}
      {tk('modified')}: {fmt(audit.updatedAt)}
      {audit.updatedByUserId ? ` ${tk('by')} ${updatedByName}` : ''}
    </p>
  )
}

export function Footer() {
  const t = useTranslations()
  const locale = useLocale()
  const { audit } = useAuditFooter()
  const { setSlot } = useFooterPaginationSlot()

  const now = new Date()
  const day = formatDate(now, locale, { weekday: 'long' })
  const date = formatDate(now, locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
  const dayLabel = day.charAt(0).toUpperCase() + day.slice(1)

  return (
    <footer className="bg-background border-t border-sidebar-border">
      <div className="flex justify-between items-center gap-4 p-4 md:px-6">
        <p className="text-[11px] text-muted-foreground">
          {dayLabel}, {date}, {t('footer.week')} {isoWeek(now)}
        </p>
        <div className="flex items-center gap-4">
          {audit ? <FooterAudit audit={audit} /> : null}
          {/* List pages portal their pagination ("Rows per page" etc.) here. */}
          <div ref={setSlot} className="flex items-center" />
        </div>
      </div>
    </footer>
  )
}
